import Phaser from 'phaser';
import { ItemRarity, RARITY_COLORS, TILE_SIZE } from '@game/protocol';

import { addTexture, context2d, createCanvas, ellipse, px } from './canvas.ts';
import { OUTLINE } from './palette.ts';

/**
 * Raridade como **acabamento**, e não só como cor de borda (PRD §15.11).
 *
 * A borda colorida do slot resolvia metade do problema: ela diz que a peça é rara, mas quem
 * a diz é a moldura, não o objeto. O sintoma aparecia no lugar mais caro possível — a Coroa
 * do dragão ancião, que cai de uma criatura de faixa 8, era desenhada pela mesma função e
 * com a mesma tinta do Elmo de couro que se compra no primeiro balcão. Um pixel de borda
 * âmbar em volta de uma calota marrom não é uma coroa; é um elmo de couro com um aviso.
 *
 * Aqui a raridade escolhe a **matéria** e o quanto a peça é enfeitada, e as duas coisas
 * crescem juntas: couro, aço, aço-crepúsculo, ouro de dragão, e o marfim do Alvorecer. É a
 * mesma ideia que já governa este atlas — usar o dado que a peça já declara em vez de uma
 * tabela paralela que envelhece —, só que aplicada ao campo que existia justamente para ser
 * visto e que ninguém estava desenhando.
 *
 * ## Por que a pedra tem a cor da borda
 *
 * O engaste de cada peça repete `RARITY_COLORS`. Não é economia de paleta: é o que ensina a
 * escada sem texto. Quem viu uma vez a borda âmbar ao lado de uma pedra âmbar lê a próxima
 * peça lendária pela pedra, mesmo com a moldura fora do campo de visão — no chão, na mesa de
 * troca, na mochila de outro jogador. A cor da borda vira vocabulário em vez de decoração.
 */

export interface Ramp {
  /** Tom principal da superfície. */
  readonly base: string;
  /** Sombra: o lado oposto à luz do noroeste, que é de onde todo este atlas se ilumina. */
  readonly dark: string;
  /** Realce: a beira batida pelo sol. */
  readonly light: string;
}

/**
 * De que a peça é feita **antes** de a raridade opinar.
 *
 * Existe porque a raridade sozinha erra em dois pontos do catálogo, e os dois são visíveis:
 * o Elmo de aço é comum e não pode sair de couro, e o Manto do conjurador é uma peça de pano
 * que não pode sair de placa só porque subiu um degrau. A matéria é a correção mínima — três
 * valores, declarados por quem conhece o item, e a raridade continua decidindo o resto.
 */
export const Material = {
  Hide: 0,
  Metal: 1,
  Cloth: 2,
} as const;

export type Material = (typeof Material)[keyof typeof Material];

const LEATHER: Ramp = { base: '#8a5f3a', dark: '#5e3f26', light: '#ad7d52' };
const STEEL: Ramp = { base: '#c3cad4', dark: '#78818e', light: '#e8eef5' };

/**
 * Aço-crepúsculo: o metal do épico.
 *
 * Cinza puxado para o violeta, e não violeta puro — a peça ainda é uma armadura, e uma
 * couraça lilás lê como fantasia de festa. O violeta entra na sombra, que é onde a paleta
 * deste jogo já joga o frio (`celShadow` em `canvas.ts`), então a peça épica parece a mesma
 * matéria das outras iluminada por outro céu. Serve às seis peças do crepúsculo, ao vazio e
 * ao ocaso, que são a mesma linha com nomes diferentes.
 */
const TWILIGHT: Ramp = { base: '#6f6394', dark: '#3b3358', light: '#a498cd' };

/** Ouro do dragão ancião: o metal do lendário. Mais sujo que o ouro de moeda, e de propósito. */
const DRAGON: Ramp = { base: '#c9922f', dark: '#7f5a15', light: '#f2c766' };

/**
 * Alvorecer: marfim quase branco, com o dourado só na sombra.
 *
 * A peça celestial não pode ser "mais ouro que o ouro" — esse degrau já é do lendário, e
 * repeti-lo mais claro daria duas peças que só se separam lado a lado. O que a separa é o
 * **valor**: ela é a única coisa clara num inventário inteiro de aço e couro, e chama
 * atenção pelo mesmo motivo que uma vela chama num quarto escuro.
 */
const DAWN: Ramp = { base: '#ecebd6', dark: '#a9a98c', light: '#fffdf2' };

const LINEN: Ramp = { base: '#b8ab8d', dark: '#867a5f', light: '#ded4bb' };
const ARCANE: Ramp = { base: '#4a63ad', dark: '#2a3a6c', light: '#7d94d9' };
const RUNIC: Ramp = { base: '#6a4fa8', dark: '#3c2a68', light: '#9d84d9' };

/** Pano das constelações: azul de noite, para o branco das estrelas ter onde aparecer. */
const STARCLOTH: Ramp = { base: '#313c72', dark: '#1a2145', light: '#5d6cad' };

export interface Finish {
  readonly rarity: ItemRarity;
  /** A superfície da peça. */
  readonly ramp: Ramp;
  /** Filete de metal nobre: gola, cinto, aro. `null` no comum, que não tem enfeite nenhum. */
  readonly trim: string | null;
  /** Pedra do engaste, na cor da raridade. `null` abaixo de `Rare`. */
  readonly gem: string | null;
  /** O ponto de luz dentro da pedra. */
  readonly gemLight: string;
}

const GOLD = '#d9b13c';
const GOLD_LIGHT = '#f2dc8a';

function rampFor(material: Material, rarity: ItemRarity): Ramp {
  if (rarity >= ItemRarity.Celestial) return DAWN;
  if (rarity >= ItemRarity.Legendary) return DRAGON;

  if (material === Material.Cloth) {
    if (rarity >= ItemRarity.Epic) return STARCLOTH;
    if (rarity >= ItemRarity.Rare) return RUNIC;
    if (rarity >= ItemRarity.Uncommon) return ARCANE;
    return LINEN;
  }

  if (rarity >= ItemRarity.Epic) return TWILIGHT;
  if (material === Material.Metal) return STEEL;

  // Couro só até o incomum: de `Rare` para cima o catálogo inteiro é placa, malha ou escama,
  // e nenhuma peça dessas se veste de marrom.
  return rarity >= ItemRarity.Rare ? STEEL : LEATHER;
}

/**
 * O acabamento de uma peça: matéria, filete, pedra e brilho, em um objeto só.
 *
 * Um objeto e não quatro parâmetros porque toda peça de equipamento precisa dos quatro, e
 * quatro argumentos posicionais na assinatura de nove funções de desenho é o tipo de coisa
 * que se troca de ordem em silêncio no primeiro ajuste.
 */
export function finish(rarity: ItemRarity | undefined, material: Material): Finish {
  const rank = rarity ?? ItemRarity.Common;
  const gem = rank >= ItemRarity.Rare ? RARITY_COLORS[rank] : null;

  return {
    rarity: rank,
    ramp: rampFor(material, rank),
    trim: trimOf(rank),
    gem,
    // O ponto de luz é a mesma cor lavada de branco, e não um branco puro: em 32 px a pedra
    // tem três pixels, e um deles branco absoluto apaga o matiz que a identifica.
    gemLight: gem ? washed(gem) : '#ffffff',
  };
}

function trimOf(rarity: ItemRarity): string | null {
  if (rarity >= ItemRarity.Celestial) return '#fff6d8';
  if (rarity >= ItemRarity.Legendary) return GOLD_LIGHT;
  if (rarity >= ItemRarity.Epic) return GOLD;
  if (rarity >= ItemRarity.Uncommon) return STEEL.light;
  return null;
}

/** A mesma cor com metade de branco por cima. Ver `Finish.gemLight`. */
function washed(color: string): string {
  const value = Number.parseInt(color.slice(1), 16);
  const channel = (shift: number): number =>
    Math.min(255, Math.round(((value >> shift) & 0xff) * 0.45 + 255 * 0.55));
  return `#${((channel(16) << 16) | (channel(8) << 8) | channel(0)).toString(16).padStart(6, '0')}`;
}

/**
 * A pedra do engaste, com garra em volta.
 *
 * Uma função e não três linhas repetidas em cada peça: elmo, couraça, anel, amuleto e escudo
 * engastam a mesma pedra, e uma pedra que muda de tamanho entre a coroa e o anel diria que
 * são de famílias diferentes quando são exatamente a mesma promessa.
 */
export function gem(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  radius: number,
  it: Finish,
): void {
  if (!it.gem) return;

  // Soquete escuro por baixo, e não o filete claro que estava aqui antes: no lendário a peça
  // **é** dourada, e uma pedra âmbar engastada em ouro claro some. Um anel escuro de um pixel
  // é como pixel art separa pedra de armação em qualquer metal — foi a Gargantilha estelar
  // que cobrou a conta, saindo como um pingente de ouro liso.
  ellipse(ctx, x, y, radius + 1, radius + 1, OUTLINE);
  if (it.trim) ellipse(ctx, x, y, radius + 0.5, radius + 0.5, it.trim);
  ellipse(ctx, x, y, radius, radius, it.gem);
  ellipse(ctx, x - radius * 0.3, y - radius * 0.3, radius * 0.42, radius * 0.42, it.gemLight);
}

/**
 * Faíscas e halo, do lendário para cima.
 *
 * Passa **depois** do contorno, e essa ordem é a razão de esta função existir separada do
 * desenho: `outline()` pinta de escuro todo pixel transparente que encoste em algo opaco, e
 * uma faísca de três pixels desenhada antes dele sai com um anel preto em volta — que a 32 px
 * é uma sujeira, não um brilho. O halo entra por `destination-over` pelo mesmo motivo ao
 * contrário: ele precisa ficar **atrás** da peça, e já não há contorno a correr sobre ele.
 *
 * O que ela não faz é animar. A animação mora onde o item é exibido — a varredura de luz no
 * inventário (`style.css`) e o pulso no chão (`render/ground-items.ts`) —, porque o ícone é
 * uma textura só e multiplicá-lo em quadros custaria memória em cada um dos 173 itens para
 * animar sete.
 */
export function rarityAura(ctx: CanvasRenderingContext2D, rarity: ItemRarity | undefined): void {
  if (rarity === undefined || rarity < ItemRarity.Legendary) return;

  const color = RARITY_COLORS[rarity];
  const celestial = rarity >= ItemRarity.Celestial;
  const c = TILE_SIZE / 2;

  // Sempre nas mesmas posições, e não sorteadas pelo id: a faísca é a assinatura da classe,
  // e uma assinatura que muda de lugar a cada item deixa de ser lida como assinatura.
  const sparks = celestial
    ? ([
        [c - 11, c - 10, 3],
        [c + 11, c - 4, 2],
        [c - 8, c + 11, 2],
        [c + 9, c + 9, 3],
      ] as const)
    : ([
        [c + 10, c - 10, 3],
        [c - 11, c + 8, 2],
      ] as const);

  for (const [x, y, size] of sparks) spark(ctx, x, y, size, celestial ? '#eafffb' : GOLD_LIGHT, color);

  ctx.save();
  ctx.globalCompositeOperation = 'destination-over';
  const glow = ctx.createRadialGradient(c, c, 3, c, c, TILE_SIZE / 2);
  glow.addColorStop(0, `${color}59`);
  glow.addColorStop(0.55, `${color}2b`);
  glow.addColorStop(1, `${color}00`);
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, TILE_SIZE, TILE_SIZE);
  ctx.restore();
}

/**
 * Estrela de quatro pontas.
 *
 * Desenhada braço a braço, com a cor **desbotando** da ponta para o centro. A primeira versão
 * pintava os quatro braços de uma cor só e o que saía era um sinal de mais — que a 32 px lê
 * como cruz de farmácia, não como brilho. O que faz o olho ver luz é o degradê: um núcleo
 * aceso e pontas que somem no fundo.
 */
function spark(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  core: string,
  halo: string,
): void {
  for (let step = size; step >= 1; step--) {
    // Alfa proporcional à distância do centro: `40` na ponta, opaco junto ao núcleo.
    const alpha = Math.round(255 * (1 - step / (size + 1)) ** 1.4)
      .toString(16)
      .padStart(2, '0');
    const tint = `${step > size / 2 ? halo : core}${alpha}`;

    px(ctx, x - step, y, 1, 1, tint);
    px(ctx, x + step, y, 1, 1, tint);
    px(ctx, x, y - step, 1, 1, tint);
    px(ctx, x, y + step, 1, 1, tint);
  }

  px(ctx, x, y, 1, 1, core);
  // Os quatro diagonais imediatos, bem fracos: é o que arredonda o núcleo e tira da estrela
  // o ar de cruz sem gastar um pixel a mais de raio.
  for (const [dx, dy] of [
    [-1, -1],
    [1, -1],
    [-1, 1],
    [1, 1],
  ] as const) {
    px(ctx, x + dx, y + dy, 1, 1, `${core}55`);
  }
}

/**
 * Halo do item no chão.
 *
 * Textura à parte, e não parte do ícone, porque é ela que o `tween` faz pulsar: o ícone
 * precisa ficar nítido enquanto a luz em volta respira, e uma coisa só não pode fazer as
 * duas. 64 px de lado para o halo passar da borda do tile — um brilho que termina exatamente
 * no quadrado do item vira um quadrado brilhante.
 */
export function rarityHaloKey(rarity: ItemRarity): string {
  return `rarity-halo-${rarity}`;
}

const HALO_SIZE = 64;

export function buildRarityHalos(scene: Phaser.Scene): void {
  for (const rarity of [ItemRarity.Legendary, ItemRarity.Celestial]) {
    const key = rarityHaloKey(rarity);
    if (scene.textures.exists(key)) continue;

    const canvas = createCanvas(HALO_SIZE, HALO_SIZE);
    const ctx = context2d(canvas);
    const c = HALO_SIZE / 2;

    const color = RARITY_COLORS[rarity];
    const glow = ctx.createRadialGradient(c, c, 2, c, c, c);
    glow.addColorStop(0, `${color}cc`);
    glow.addColorStop(0.4, `${color}55`);
    glow.addColorStop(1, `${color}00`);
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, HALO_SIZE, HALO_SIZE);

    addTexture(scene, key, canvas);
  }
}
